import { EmailOutlined as Email } from "@mui/icons-material";
import { Box, Button, InputAdornment } from "@mui/material";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { type FC, type FormEventHandler, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ValidationTextFieldProps } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { SystemValidationTextField } from "../../components/UIToolkit/Inputs/SystemValidationTextField";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { usePhoneValidation } from "../../hooks/usePhoneValidation";
import { backendService } from "../../services/backend.service";
import { formatUsernameInput, isValidEmail } from "../../utils/phoneAndEmailUtils";
import { SigninLayout } from "./common";

function useSendResetPassword(): UseMutationResult<void, Error, string, unknown> {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const navigate = useNavigate();
	const { track, trackError } = useAnalytics();

	return useMutation<void, Error, string>({
		mutationKey: ["useSendResetPassword"],
		mutationFn: async (emailOrPhoneNumber) => {
			try {
				track("forgot_password", { status: "start", emailOrPhoneNumber });
				await backendService.sendNurseReset({ emailOrPhoneNumber });

				showSnackbar(t("reset.link.sent"), "success");
				track("forgot_password", { status: "success", emailOrPhoneNumber });

				navigate("/");
			} catch (e) {
				trackError("forgot_password", {
					error: (e as Error).message,
					emailOrPhoneNumber,
				});

				if (e instanceof AxiosError && typeof e.response?.data?.detail === "string") {
					showSnackbar(t(e.response.data.detail), "error");
				} else if (e instanceof Error) {
					showSnackbar(e.message, "error");
				} else {
					showSnackbar(t("reset.link.error"), "error");
				}
			}
		},
		throwOnError: false,
	});
}

function emailOrPhoneValidationCallback(value: string, phoneValidation: ValidationTextFieldProps): boolean {
	if (value.length === 0) return true;
	// Accept email, phone, or letters-only username
	return isValidEmail(value) || phoneValidation.validationCallback(value) || /^[a-zA-Z\s]+$/.test(value);
}

export const ForgotPassword: FC = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const phoneValidation = usePhoneValidation();

	const { mutateAsync: sendResetPassword, isPending } = useSendResetPassword();
	const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
		async (e) => {
			e.preventDefault();

			const formData = new FormData(e.currentTarget);
			const rawUsername = formData.get("username")?.toString().trim();
			if (!rawUsername) {
				showSnackbar(t("email.phone.must.not.be.empty"), "warning");
				return;
			}

			// If it's an email, lowercase it; if it's a phone number, keep the formatting
			const username = rawUsername.includes("@") ? rawUsername.toLowerCase() : rawUsername;
			await sendResetPassword(username);
		},
		[sendResetPassword, showSnackbar, t],
	);

	const usernameValidation = useMemo<ValidationTextFieldProps>(
		() => ({
			showValidWhenValid: "never",
			performValidation: "onlyAfterInitialChange",
			helperText: {
				invalid: t("invalid.email.or.phone.format"),
			},
			validationCallback: (value: string) => emailOrPhoneValidationCallback(value, phoneValidation),
		}),
		[t, phoneValidation],
	);

	const [username, setUsername] = useState("");

	const onUsernameChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const formattedValue = formatUsernameInput(e.target.value);
		setUsername(formattedValue);
	}, []);

	return (
		<SigninLayout title="Forgot Password">
			<Box component="form" sx={{ display: "contents" }} onSubmit={onSubmit}>
				<SystemValidationTextField
					placeholder="Username (email or phone number)"
					variant="outlined"
					required
					value={username}
					onChange={onUsernameChange}
					validation={usernameValidation}
					autoFocus
					injectedProps={{
						name: "username",
						slotProps: {
							input: {
								startAdornment: (
									<InputAdornment position="start">
										<Email />
									</InputAdornment>
								),
							},
						},
					}}
				/>
				<Button type="submit" variant="contained" loading={isPending} disabled={!username.trim() || !emailOrPhoneValidationCallback(username, phoneValidation)}>
					Send Reset Email
				</Button>
			</Box>
		</SigninLayout>
	);
};
